import { createDraftSafeSelector } from "@reduxjs/toolkit";
import moment from "moment";
import { budgetDetailsAccumulatedCostDataSelector } from "@/store/budgets/selectors/details-drawer/accumulated-cost/budgetDetailsAccumulatedCostDataSelector";
import {
  HighchartsChartDataSeriesOption,
  HighchartsChartDataType,
} from "@/storybook/c8r-highcharts/common/utils/types/common";
import { budgetSeriesName } from "@/components/pages/common/billing-explorer/utils/addBudgetLineIntoBillingExplorerChartData";
import { budgetDetailsAccumulatedCostBudgetSelector } from "@/store/budgets/selectors/details-drawer/accumulated-cost/budgetDetailsAccumulatedCostBudgetSelector";

export const budgetDetailsAccumulatedCostChartDataSelector =
  createDraftSafeSelector(
    [
      budgetDetailsAccumulatedCostDataSelector,
      budgetDetailsAccumulatedCostBudgetSelector,
    ],
    (accumulatedCost, budget): HighchartsChartDataType | null => {
      const { costs } = accumulatedCost ?? {};

      if (!costs || !budget) {
        return null;
      }

      const categories: string[] = costs.map((cost) => cost.date ?? "");
      const currentDate = moment();

      let prevCost = 0;
      const processedData = costs.map(({ cost, date }) => {
        const costValue = cost ?? 0;
        const budgetAvg = (costValue + prevCost) / 2;
        prevCost = costValue;

        return {
          cost: costValue,
          isBudgeted: budgetAvg < budget,
          isOverspent: budgetAvg >= budget,
          isForecast: currentDate.isSameOrBefore(moment(date).add(1, "day")),
        };
      });

      const budgetedSeries: HighchartsChartDataSeriesOption = {
        name: "Budgeted",
        color: "#2196F3",
        type: "area",
        marker: { enabled: false },
        fillColor: {
          linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
          stops: [
            [0, "#2196F340"],
            [1, "#2196F30D"],
          ],
        },
        data: processedData.map((item) => {
          if (!item.isBudgeted || item.isForecast) {
            return { y: null };
          }

          return { y: item.cost };
        }),
      };

      const overspentSeries: HighchartsChartDataSeriesOption = {
        name: "Overspent",
        color: "#F44336",
        type: "area",
        marker: { enabled: false },
        fillColor: {
          linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
          stops: [
            [0, "#F4433640"],
            [1, "#F443360D"],
          ],
        },
        data: processedData.map((item, index, array) => {
          if (
            item.isBudgeted &&
            index < array.length - 1 &&
            array[index + 1].isOverspent
          ) {
            return { y: item.cost };
          }

          if (!item.isOverspent || item.isForecast) {
            return { y: null };
          }

          return { y: item.cost };
        }),
      };

      const budgetSeries: HighchartsChartDataSeriesOption = {
        name: budgetSeriesName,
        type: "line",
        color: "red",
        dashStyle: "Dash",
        data: Array(categories.length).fill(budget),
        marker: { enabled: false },
        lineWidth: 2,
        purpose: "visualization",
      };

      const forecastSeries: HighchartsChartDataSeriesOption = {
        name: "Forecast",
        type: "line",
        color: "#82B1FF",
        dashStyle: "Dash",
        data: processedData.map((item, index, array) => {
          if (
            (item.isOverspent || item.isBudgeted) &&
            index < array.length - 1 &&
            array[index + 1].isForecast
          ) {
            return { y: item.cost };
          }

          if (!item.isForecast) {
            return { y: null };
          }

          return { y: item.cost };
        }),
        marker: { enabled: false },
        lineWidth: 2,
      };

      const allSeries = [
        budgetedSeries,
        overspentSeries,
        budgetSeries,
        forecastSeries,
      ];

      const seriesWithData = allSeries.filter((series) => {
        if (series.name === "Budgeted" || series.name === budgetSeriesName) {
          return true;
        }

        return series.data.some((value) => value !== null);
      });

      return {
        categories,
        series: seriesWithData,
      };
    },
  );
